import Asset from 'types/asset';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Utilities from 'utils/utilities';
import SelectOption from 'types/selectOption';
import frequencyConfList from 'types/frequencyConf';
import Tag from 'types/tag';
import { v4 as uuidv4 } from 'uuid';
import TagService from 'services/TagService';
import useAuth from 'hooks/useAuth';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';

interface ChildComponentProps {
  asset?: Asset;
  onAdd(asset: Asset): any;
  onClose(): any;
}

// Default threshold values
const DEFAULT_THRESHOLDS = {
  minTemperature: -20,
  maxTemperature: 60,
  minHumidity: 0,
  maxHumidity: 90,
  minPressure: 600,
  maxPressure: 1100
};

const AssetAdd: FC<ChildComponentProps> = (props) => {
  const { asset, onAdd, onClose } = props;
  const [open, setOpen] = useState(false);
  const [currentAsset, setCurrentAsset] = useState(asset);
  const { t } = useTranslation();
  const { user } = useAuth();
  const utilities = new Utilities();
  const theme = useTheme();
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const frequencyConf: SelectOption[] = frequencyConfList(t);
  const [tagToSearch, setTagToSearch] = useState<string>('');
  const tagService = new TagService();
  const [removedTags, setRemovedTags] = useState<Tag[]>([]);

  // Stepper state - MODIFICATO: aggiunto un terzo step
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    { label: t('BASIC_INFORMATION'), icon: '📋' },
    { label: t('STARTUP_PARAMETERS'), icon: '⚡' }, // NUOVO STEP
    { label: t('OPERATING_PARAMETERS'), icon: '⚙️' }
  ];

  // Threshold states with default values
  const [maxCelsiusTemperatureThreshold, setMaxCelsiusTemperatureThreshold] = useState<string>(DEFAULT_THRESHOLDS.maxTemperature.toString());
  const [maxHumidityThreshold, setMaxHumidityThreshold] = useState<string>(DEFAULT_THRESHOLDS.maxHumidity.toString());
  const [maxPressureThreshold, setMaxPressureThreshold] = useState<string>(DEFAULT_THRESHOLDS.maxPressure.toString());
  const [minCelsiusTemperatureThreshold, setMinCelsiusTemperatureThreshold] = useState<string>(DEFAULT_THRESHOLDS.minTemperature.toString());
  const [minHumidityThreshold, setMinHumidityThreshold] = useState<string>(DEFAULT_THRESHOLDS.minHumidity.toString());
  const [minPressureThreshold, setMinPressureThreshold] = useState<string>(DEFAULT_THRESHOLDS.minPressure.toString());

  // Luminosity alert state
  const [isLuminosityAlertActive, setIsLuminosityAlertActive] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (asset === undefined) {
        setOpen(false);
      } else {
        const assetCopy = {
          ...currentAsset!,
          acceleration: 2,
          frequency: 2,
          // Initialize with default threshold values
          maxCelsiusTemperatureThreshold: DEFAULT_THRESHOLDS.maxTemperature,
          maxHumidityThreshold: DEFAULT_THRESHOLDS.maxHumidity,
          maxPressureThreshold: DEFAULT_THRESHOLDS.maxPressure,
          minCelsiusTemperatureThreshold: DEFAULT_THRESHOLDS.minTemperature,
          minHumidityThreshold: DEFAULT_THRESHOLDS.minHumidity,
          minPressureThreshold: DEFAULT_THRESHOLDS.minPressure,
          isLuminosityAlertActive: false
        };
        setCurrentAsset(assetCopy);
        setOpen(true);
        setActiveStep(0); // Reset stepper
        await fetchTags();
        setSelectedTags(asset.tags);
      }
    })();
  }, [asset]);

  useEffect(() => {
    const e = {
      ...currentAsset!,
      tags: selectedTags,
      maxCelsiusTemperatureThreshold: maxCelsiusTemperatureThreshold ? parseFloat(maxCelsiusTemperatureThreshold) : DEFAULT_THRESHOLDS.maxTemperature,
      maxHumidityThreshold: maxHumidityThreshold ? parseFloat(maxHumidityThreshold) : DEFAULT_THRESHOLDS.maxHumidity,
      maxPressureThreshold: maxPressureThreshold ? parseFloat(maxPressureThreshold) : DEFAULT_THRESHOLDS.maxPressure,
      minCelsiusTemperatureThreshold: minCelsiusTemperatureThreshold ? parseFloat(minCelsiusTemperatureThreshold) : DEFAULT_THRESHOLDS.minTemperature,
      minHumidityThreshold: minHumidityThreshold ? parseFloat(minHumidityThreshold) : DEFAULT_THRESHOLDS.minHumidity,
      minPressureThreshold: minPressureThreshold ? parseFloat(minPressureThreshold) : DEFAULT_THRESHOLDS.minPressure,
      isLuminosityAlertActive: isLuminosityAlertActive
    };
    setCurrentAsset(e);
  }, [selectedTags, maxCelsiusTemperatureThreshold, maxHumidityThreshold, maxPressureThreshold, minCelsiusTemperatureThreshold, minHumidityThreshold, minPressureThreshold, isLuminosityAlertActive]);

  useEffect(() => {
    (async () => {
      if (asset !== undefined) {
        await fetchTags();
      }
    })();
  }, [asset, tagToSearch]);

  const fetchTags = useCallback(
    debounce(async () => {
      if (asset && asset.id) {
        const tagResult = await tagService.getAssetTags(tagToSearch, asset!.id, user?.tenantId!);
        setTags(tagResult);
      }
    }, 700),
    [tagToSearch, user?.tenantId, asset]
  );

  const handleTagChange = (_: React.SyntheticEvent, newValue: Tag[]) => {
    const uniqueTags = newValue
    .map((tag) => ({ ...tag, name: tag.name.trim() }))
    .filter((tag, index, self) => index === self.findIndex((t) => t.name.toLowerCase() === tag.name.toLowerCase()));
    setSelectedTags(uniqueTags);

    const removedTags = selectedTags.filter((tag) => !uniqueTags.some((selectedTag) => selectedTag.id === tag.id));
    setRemovedTags((prev) => {
      const updatedTags = [...prev, ...removedTags].filter(
        (tag, index, self) => index === self.findIndex((t) => t.id === tag.id || t.name.trim().toLowerCase() === tag.name.trim().toLowerCase())
      );
      return updatedTags;
    });
    setTags((prev) => {
      const uniqueTags = [...prev, ...removedTags].filter(
        (tag, index, self) => index === self.findIndex((t) => t.name.trim().toLowerCase() === tag.name.trim().toLowerCase())
      );
      return uniqueTags;
    });
  };

  const handleTagInputChange = (event: React.KeyboardEvent | React.FocusEvent, value: string) => {
    const trimmedValue = value.trim().toLowerCase();
    const containsComma = trimmedValue.includes(',');
    if (containsComma) {
      dispatch(startSnackbar(t('ERROR_ADDING_TAG_REMOVE_COMMA'), 'error'));
      return;
    }

    setTagToSearch(value);

    // Verifica se l'input è valido
    if (trimmedValue && !containsComma && (event.type === 'blur' || (event as React.KeyboardEvent).key === 'Enter')) {
      const existingTag = tags.find((tag) => tag.name.trim().toLowerCase() === trimmedValue);
      const alreadySelected = selectedTags.some((tag) => tag.name.trim().toLowerCase() === trimmedValue);

      if (!alreadySelected) {
        if (existingTag) {
          setRemovedTags((prev) => prev.filter((tag) => tag.id !== existingTag.id));
          setSelectedTags((prev) => [...prev, existingTag]);
        } else {
          const newTag: Tag = { id: uuidv4(), name: trimmedValue };
          setTags((prev) => [...prev, newTag]);
          setSelectedTags((prev) => [...prev, newTag]);
        }
      }
    }
  };

  const handleFrequency = (e: any) => {
    const { value } = utilities.getFormValue(e);
    const assetCopy = {
      ...currentAsset!,
      frequency: parseFloat(value)
    };
    setCurrentAsset(assetCopy);
  };

  const handleThresholdChange = (value: string, setter: React.Dispatch<React.SetStateAction<string>>, propertyName: string) => {
    setter(value);
    const updatedAsset = {
      ...currentAsset!,
      [propertyName]: value === '' ? undefined : parseFloat(value)
    };
    setCurrentAsset(updatedAsset);
  };

  const handleLuminosityAlertChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsLuminosityAlertActive(checked);
  };

  const handleNext = () => {
    // Validate step 0 (basic information)
    if (activeStep === 0) {
      if (!currentAsset?.name || currentAsset.name.trim() === '') {
        dispatch(startSnackbar(t('NAME_IS_MANDATORY'), 'error'));
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setCurrentAsset(undefined);
    setActiveStep(0);
    onClose();
  };

  const handleAdd = () => {
    // Final validation
    if (!currentAsset?.name || currentAsset.name.trim() === '') {
      dispatch(startSnackbar(t('NAME_IS_MANDATORY'), 'error'));
      return;
    }
    onAdd(currentAsset!);
    setActiveStep(0);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required size="small">
                <InputLabel>{t('NAME')}</InputLabel>
                <OutlinedInput
                  defaultValue={asset?.name}
                  onChange={(d) => {
                    const e = {
                      ...currentAsset!,
                      name: d.target.value
                    };
                    setCurrentAsset(e);
                  }}
                  type="text"
                  label={t('NAME')}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="size-small-filled-multi"
                size="small"
                sx={{
                  '& .MuiFilledInput-root': { backgroundColor: '#fff' },
                  '& .MuiFilledInput-root:hover': { backgroundColor: '#fff' },
                  '& #size-small-filled-multi': {
                    backgroundColor: 'secondary'
                  }
                }}
                options={[
                  ...tags,
                  ...removedTags.filter((tag) => !tags.some((existingTag) => existingTag.name.trim().toLowerCase() === tag.name.trim().toLowerCase()))
                ].filter((tag) => !selectedTags.some((selected) => selected.name.trim().toLowerCase() === tag.name.trim().toLowerCase()))}
                getOptionLabel={(option) => option.name}
                value={selectedTags}
                onChange={handleTagChange}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip color="secondary" key={key} label={option.name} size="small" {...tagProps} />;
                  })
                }
                onInputChange={(event: any, value, reason) => {
                  if (reason === 'input') handleTagInputChange(event, value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    onKeyDown={(event: any) => handleTagInputChange(event, event.target.value)}
                    onBlur={(event) => handleTagInputChange(event, event.target.value)}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            {/* Frequency Parameter */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                🔄 {t('FREQUENCY')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t('FREQUENCY')}</InputLabel>
                <Select
                  size="small"
                  label={t('FREQUENCY')}
                  name="frequency"
                  onChange={handleFrequency}
                  value={Math.floor(currentAsset?.frequency || 10)}
                  defaultValue={10}
                >
                  {frequencyConf.map((frequency) => (
                    <MenuItem value={frequency.value} key={frequency.value}>
                      {t(frequency.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Acceleration Parameter */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                ⚡ {t('ACCELERATION')} (G)
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t('ACCELERATION')}</InputLabel>
                <OutlinedInput
                  onBlur={(e) => {
                    const inputValue = parseFloat(e.target.value).toFixed(2);
                    e.target.value = inputValue;
                    const updatedAsset = {
                      ...currentAsset!,
                      acceleration: parseFloat(inputValue)
                    };
                    setCurrentAsset(updatedAsset);
                  }}
                  defaultValue={asset?.acceleration?.toFixed(2)}
                  onChange={(d) => {
                    const inputValue = d.target.value;
                    const e = {
                      ...currentAsset!,
                      acceleration: inputValue === '' ? undefined : Number(Number(inputValue).toFixed(2))
                    };
                    setCurrentAsset(e);
                  }}
                  type="number"
                  label={t('ACCELERATION')}
                  inputProps={{ step: '1', min: '0' }}
                />
              </FormControl>
            </Grid>
          </Grid>
        );

      // STEP 2: Parametri Operativi (ex step 1)
      case 2:
        return (
          <Grid container spacing={0.5}>
            {/* Luminosity Alert Switch */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                💡 {t('LUMINOSITY_PARAMETERS')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isLuminosityAlertActive}
                    onChange={handleLuminosityAlertChange}
                    color="primary"
                  />
                }
                label={t('LUMINOSITY_ALERT_ACTIVE')}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                  }
                }}
              />
            </Grid>

            {/* Temperature Parameters */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                🌡️ {t('TEMPERATURE_PARAMETERS')} (°C)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MIN_TEMPERATURE_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={minCelsiusTemperatureThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMinCelsiusTemperatureThreshold, 'minCelsiusTemperatureThreshold')}
                  type="number"
                  inputProps={{ step: '1' }}
                  label={t('MIN_TEMPERATURE_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minTemperature}°C`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MAX_TEMPERATURE_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={maxCelsiusTemperatureThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMaxCelsiusTemperatureThreshold, 'maxCelsiusTemperatureThreshold')}
                  type="number"
                  inputProps={{ step: '1' }}
                  label={t('MAX_TEMPERATURE_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxTemperature}°C`}
                  size="small"
                />
              </FormControl>
            </Grid>

            {/* Humidity Parameters */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                💧 {t('HUMIDITY_PARAMETERS')} (%)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MIN_HUMIDITY_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={minHumidityThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMinHumidityThreshold, 'minHumidityThreshold')}
                  type="number"
                  inputProps={{ step: '1', min: '0', max: '100' }}
                  label={t('MIN_HUMIDITY_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minHumidity}%`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MAX_HUMIDITY_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={maxHumidityThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMaxHumidityThreshold, 'maxHumidityThreshold')}
                  type="number"
                  inputProps={{ step: '1', min: '0', max: '100' }}
                  label={t('MAX_HUMIDITY_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxHumidity}%`}
                  size="small"
                />
              </FormControl>
            </Grid>

            {/* Pressure Parameters */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                📊 {t('PRESSURE_PARAMETERS')} (hPa)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MIN_PRESSURE_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={minPressureThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMinPressureThreshold, 'minPressureThreshold')}
                  type="number"
                  inputProps={{ step: '1' }}
                  label={t('MIN_PRESSURE_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minPressure} hPa`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('MAX_PRESSURE_PARAMETER')}</InputLabel>
                <OutlinedInput
                  value={maxPressureThreshold}
                  onChange={(e) => handleThresholdChange(e.target.value, setMaxPressureThreshold, 'maxPressureThreshold')}
                  type="number"
                  inputProps={{ step: '1' }}
                  label={t('MAX_PRESSURE_PARAMETER')}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxPressure} hPa`}
                  size="small"
                />
              </FormControl>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth="md" fullWidth>
        <DialogTitle id="alert-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{t('ADD_NEW_ASSET')}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ paddingTop: '20px!important' }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{step.icon}</span>
                    <Typography variant="body2">{step.label}</Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ mt: 2, mb: 3 }}>{renderStepContent(activeStep)}</Box>

          {activeStep === 0 && (
            <Typography marginTop="15px" sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
              * {t('MANDATORY_FIELDS')}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleClose}
            style={{
              border: '2px solid',
              borderColor: theme.palette.secondary.main,
              borderRadius: 7
            }}
          >
            <Typography sx={{ color: theme.palette.primary.main }}>{t('CANCEL')}</Typography>
          </Button>

          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              style={{
                border: '2px solid',
                borderColor: theme.palette.grey[400],
                borderRadius: 7,
                marginLeft: 8
              }}
            >
              <Typography sx={{ color: theme.palette.text.primary }}>{t('BACK')}</Typography>
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              style={{
                border: '2px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: 7,
                background: theme.palette.primary.main,
                marginLeft: 8
              }}
            >
              <Typography style={{ color: '#fff' }}>{t('NEXT')}</Typography>
            </Button>
          ) : (
            <Button
              onClick={handleAdd}
              style={{
                border: '2px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: 7,
                background: theme.palette.primary.main,
                marginLeft: 8
              }}
            >
              <Typography style={{ color: '#fff' }}>{t('ADD')}</Typography>
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetAdd;
